import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Grid, MenuItem, Menu, AppBar, Tabs, Tab, Box } from '@mui/material';
import OfferPartnerFormContainer from 'features/offer/edit/client/form/components/OfferPartnerFormContainer';
import OfferPartnerConsentContainer from 'features/offer/edit/client/consent/components/OfferPartnerConsentContainer';
import OfferPartnerVerificationContainer from 'features/offer/edit/client/verification/components/OfferPartnerVerificationContainer';
import OfferPartnerIncomeTypeContainer from 'features/offer/edit/client/income/components/OfferPartnerIncomeTypeContainer';
import OfferPartnerMonthlyObligationsContainer from 'features/offer/edit/client/monthlyObligations/components/OfferPartnerMonthlyObligationsContainer';
import OfferPartnerContactPersonsContainer from 'features/offer/edit/client/contactPersons/components/OfferPartnerContactPersonsContainer';
import OfferPartnerActivityFieldsContainer from 'features/offer/edit/client/activities/components/ActivityFieldsContainer';
import OfferPartnerShareholderContainer from 'features/offer/edit/client/shareholders/components/OfferPartnerShareholderContainer';
import OfferPartnerPropertyContainer from 'features/offer/edit/client/properties/components/OfferPartnerPropertyContainer';
import OfferPartnerContactModesContainer from 'features/offer/edit/client/contactModes/components/OfferPartnerContactModesContainer';
import OfferPartnerBankAccountContainer from 'features/offer/edit/client/bankAccount/components/OfferPartnerBankAccountContainer';
import OfferPartnerListQuestionnaireContainer from 'features/offer/edit/client/questionnaire/components/edit/OfferPartnerListQuestionnaireContainer';
import PartnerRelationsContainer from 'components/offer/edit/partnerRelations/PartnerRelationsContainer';
import ReorderComponentsContainer from 'components/common/reorderContainer/ReorderComponentsContainer';
import { loadPartnerIncomeTypes } from 'features/offer/edit/client/income/actionCreators';
import {
  loadDocumentPartner,
  deleteAssociatedPartner,
  loadDocumentAssociatedPartners
} from 'features/offer/edit/client/form/actionCreators';
import { defaultPartnerId } from 'features/common/selectors';
import { ApplicationFeaturesContext } from 'providers/ApplicationFeaturesContext';
import { env } from 'utils/env';
import { getPartnerFileHeader } from 'features/partnerFile/selectors';
import documentTypeIds from 'constants/documentTypeIds';
import { RightsContext } from 'providers/RightsContext';
import { defaultComponentsOrderForPF, defaultComponentsOrderForPFA, defaultComponentsOrderForPJ } from 'features/partnerFile/constants';

export const PartnerFileContainer = ({ documentId, siteId, partnerId, history, changeIndex, setChangeIndex }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    clientIncomeSourcesRead,
    clientMonthlyObligationsRead,
    clientVerificationsRead,
    partnerQuestionnaireRead,
    clientShareholdersRead,
    clientBankaccountRead,
    clientContactinfoRead,
    clientContactmodeRead,
    clientActivityfieldsRead,
    clientShareholdersWrite,
    clientBankaccountWrite,
    clientContactinfoWrite,
    clientConsentsRead,
    clientRelationsRead,
    clientPropertiesRead,
    masterPartnerApp,
    masterPartnerRead
  } = useContext(RightsContext);
  const { enablePartnerRelations } = useContext(ApplicationFeaturesContext);
  const [anchorEl, setAnchorEl] = useState(null);

  const { content: headerInfo } = useSelector(state => getPartnerFileHeader(state, documentId, siteId), shallowEqual);

  const key = `${documentId}-${siteId}`;
  const partner = useSelector(state => state.partnerFiles.item[key], shallowEqual);
  const associatedPartnersLoaded = useSelector(state => state.currentOffer.associatedPartners[key]?.loaded || false, shallowEqual);
  const associatedPartnerList = useSelector(state => state.entities.associatedPartners[key] || [], shallowEqual);

  const documentPartnerAllowedRoles = useSelector(state => state.params.items.documentPartnerAllowedRoles?.split(';'), shallowEqual);
  const allowedRolesAssociatedPartners = useMemo(
    () => associatedPartnerList.filter(p => documentPartnerAllowedRoles.includes(p.partnerRoleCode)),
    [associatedPartnerList, documentPartnerAllowedRoles]
  );
  const associatedPartners = useMemo(
    () => associatedPartnerList.filter(p => !documentPartnerAllowedRoles.includes(p.partnerRoleCode)),
    [associatedPartnerList, documentPartnerAllowedRoles]
  );
  const isAssociatedDocPartner = useMemo(() => associatedPartners.some(p => p.partnerId === partnerId), [associatedPartners, partnerId]);
  const selectedPartnerName = useMemo(() => {
    const partner = associatedPartners.find(partner => partner.partnerId === partnerId);
    return partner ? partner.partnerRole + ': ' + partner.name : '';
  }, [associatedPartners, partnerId]);

  const loadAssociatedPartner = useCallback(
    async partnerId => {
      if (clientIncomeSourcesRead) dispatch(loadPartnerIncomeTypes(documentId, siteId, partnerId));
      dispatch(loadDocumentPartner(documentId, siteId, partnerId, documentTypeIds.PartnerFile));
    },
    [dispatch, documentId, siteId, clientIncomeSourcesRead]
  );

  useEffect(() => {
    if (associatedPartnersLoaded) return;
    dispatch(loadDocumentAssociatedPartners(documentId, siteId));
  }, [documentId, siteId, dispatch, associatedPartnersLoaded]);

  useEffect(() => {
    if (!partnerId) return;
    loadAssociatedPartner(partnerId);
  }, [partnerId, loadAssociatedPartner]);

  useEffect(() => {
    if (!partnerId || isAssociatedDocPartner) return;
    setChangeIndex(allowedRolesAssociatedPartners.findIndex(a => a.partnerId === partnerId) + 1);
  }, [partnerId, isAssociatedDocPartner, allowedRolesAssociatedPartners, setChangeIndex]);

  useEffect(() => {
    if (!partnerId || !associatedPartnersLoaded || !isAssociatedDocPartner) return;
    setChangeIndex(allowedRolesAssociatedPartners.length + 2);
  }, [partnerId, associatedPartnersLoaded, isAssociatedDocPartner, allowedRolesAssociatedPartners.length, setChangeIndex]);

  const handleSelectAssociatedPartner = event => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const openDocumentPartner = partnerId => e => {
    e.stopPropagation();
    openAssociatedPartner(partnerId);
  };
  const openMasterPartner = masterPartnerId => e => {
    e.stopPropagation();
    if (!masterPartnerApp || !masterPartnerRead) return;
    window.open(`${env.REACT_APP_LSNG_MASTERPARTNER_URL}/partners/${masterPartnerId}`, '_blank');
  };

  const openClient = () => {
    setChangeIndex(0);
    history.push(`/partner-files/${documentId}/${siteId}`);
  };
  const openAssociatedPartner = partnerId => {
    setAnchorEl(null);
    history.push(`/partner-files/${documentId}/${siteId}/partner/${partnerId}`);
  };

  const handleRemovePartner = async data => {
    dispatch(deleteAssociatedPartner(data));
    setChangeIndex(0);
    history.push(`/partner-files/${documentId}/${siteId}`);
  };

  const getReorderJsonName = partner => {
    if (partner?.isPF) return 'partnerFile_client_forPF';
    else if (partner?.isPFA) return 'partnerFile_client_forPFA';
    else return 'partnerFile_client_forPJ';
  };

  const getDefaultComponentsOrder = partner => {
    if (partner?.isPF) return defaultComponentsOrderForPF;
    else if (partner?.isPFA) return defaultComponentsOrderForPFA;
    else return defaultComponentsOrderForPJ;
  };

  const getComponent = (componentName, reorderEnabled, partner) => {
    switch (componentName) {
      case 'OfferPartnerFormContainer':
        return (
          <OfferPartnerFormContainer
            documentId={documentId}
            siteId={siteId}
            partnerId={partner?.partnerId || defaultPartnerId}
            onDelete={() => {}}
            documentTypeId={headerInfo.documentTypeId}
          />
        );

      case 'OfferPartnerIncomeTypeContainer':
        return (
          (reorderEnabled || (partner?.partnerId && clientIncomeSourcesRead)) && (
            <OfferPartnerIncomeTypeContainer
              id={'partnerFile_IncomeTypeCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={partner.partnerId}
            />
          )
        );

      case 'OfferPartnerMonthlyObligationsContainer':
        return (
          (reorderEnabled || (partner?.partnerId && clientMonthlyObligationsRead)) && (
            <OfferPartnerMonthlyObligationsContainer
              id={'partnerFile_MonthlyObligationsCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={partner.partnerId}
            />
          )
        );

      case 'OfferPartnerContactPersonsContainer':
        return (
          (reorderEnabled || (partner?.partnerId && clientContactinfoRead)) && (
            <OfferPartnerContactPersonsContainer
              id={'partnerFile_ContactPersonsCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={partner?.partnerId}
              isAssociatedPartner={false}
              openMasterPartner={openMasterPartner}
              openDocumentPartner={openDocumentPartner}
              masterPartnerApp={masterPartnerApp && masterPartnerRead}
              clientContactinfoWrite={clientContactinfoWrite}
            />
          )
        );

      case 'OfferPartnerContactInfoContainer':
        return (
          (reorderEnabled || (partner?.partnerId && clientContactmodeRead)) && (
            <OfferPartnerContactModesContainer id={'partnerFile_ContactInfoCard'} partnerId={partner?.partnerId} />
          )
        );

      case 'OfferPartnerBankAccountContainer':
        return (
          (reorderEnabled || (partner?.partnerId && clientBankaccountRead)) && (
            <OfferPartnerBankAccountContainer
              id={'partnerFile_BankAccountCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={partner.partnerId}
              clientBankaccountWrite={clientBankaccountWrite}
            />
          )
        );

      case 'OfferPartnerPropertyContainer':
        return (
          (reorderEnabled || (partner?.partnerId && clientPropertiesRead)) && (
            <OfferPartnerPropertyContainer
              id={'partnerFile_PropertyCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={partner.partnerId}
            />
          )
        );

      case 'OfferPartnerConsentContainer':
        return (
          (reorderEnabled || (partner?.partnerId && clientConsentsRead)) && (
            <OfferPartnerConsentContainer
              id={'partnerFile_ConsentCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={partner.partnerId}
              onPrintClick={() => {}}
            />
          )
        );

      case 'OfferPartnerListQuestionnaireContainer':
        return (
          (reorderEnabled || (partner?.partnerId && partnerQuestionnaireRead)) && (
            <OfferPartnerListQuestionnaireContainer
              id={'partnerFile_QuestionnaireCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={partner.clientId}
              history={history}
              onChangeActiveStep={() => {}}
            />
          )
        );

      case 'PartnerRelationsContainer':
        return (
          (reorderEnabled || (clientRelationsRead && enablePartnerRelations)) && (
            <PartnerRelationsContainer
              id={'partnerFile_RelationsCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={partner?.partnerId}
              startsExpanded={false}
            />
          )
        );

      case 'OfferPartnerShareholderContainer':
        return (
          (reorderEnabled || (partner?.partnerId && clientShareholdersRead)) && (
            <OfferPartnerShareholderContainer
              id={'partnerFile_ShareholderCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={partner.partnerId}
              isAssociatedPartner={false}
              openMasterPartner={openMasterPartner}
              openDocumentPartner={openDocumentPartner}
              clientShareholdersWrite={clientShareholdersWrite}
              masterPartnerApp={masterPartnerApp && masterPartnerRead}
            />
          )
        );

      case 'OfferPartnerActivityFieldsContainer':
        return (
          (reorderEnabled || (partner?.partnerId && clientActivityfieldsRead)) && (
            <OfferPartnerActivityFieldsContainer
              id={'partnerFile_ActivityFieldsCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={partner.partnerId}
            />
          )
        );

      case 'OfferPartnerVerificationContainer':
        return (
          (reorderEnabled || (partner?.partnerId && clientVerificationsRead)) && (
            <OfferPartnerVerificationContainer
              id={'partnerFile_VerificationCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={partner.partnerId}
            />
          )
        );

      default:
        return null;
    }
  };

  const getAssociatedPartnerComponent = (componentName, reorderEnabled, partner) => {
    switch (componentName) {
      case 'OfferPartnerFormContainer':
        return (
          <OfferPartnerFormContainer
            documentId={documentId}
            siteId={siteId}
            partnerId={partner.partnerId}
            isAssociatedPartner={true}
            isRealBeneficiary={false}
            onDelete={handleRemovePartner}
            documentTypeId={headerInfo.documentTypeId}
          />
        );

      case 'OfferPartnerIncomeTypeContainer':
        return (
          (reorderEnabled || clientIncomeSourcesRead) && (
            <OfferPartnerIncomeTypeContainer
              id={'partnerFile_IncomeTypeCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={partner.partnerId}
              isAssociatedPartner={true}
            />
          )
        );

      case 'OfferPartnerMonthlyObligationsContainer':
        return (
          (reorderEnabled || clientMonthlyObligationsRead) && (
            <OfferPartnerMonthlyObligationsContainer
              id={'partnerFile_MonthlyObligationsCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={partner.partnerId}
              isAssociatedPartner={true}
            />
          )
        );

      case 'OfferPartnerContactPersonsContainer':
        return (
          (reorderEnabled || clientContactinfoRead) && (
            <OfferPartnerContactPersonsContainer
              id={'partnerFile_ContactPersonsCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={partner.partnerId}
              isAssociatedPartner={true}
              openDocumentPartner={openDocumentPartner}
              openMasterPartner={openMasterPartner}
              masterPartnerApp={masterPartnerApp && masterPartnerRead}
              clientContactinfoWrite={clientContactinfoWrite}
            />
          )
        );

      case 'OfferPartnerContactInfoContainer':
        return (
          (reorderEnabled || clientContactmodeRead) && (
            <OfferPartnerContactModesContainer id={'partnerFile_ContactInfoCard'} partnerId={partner.partnerId} />
          )
        );

      case 'OfferPartnerBankAccountContainer':
        return (
          (reorderEnabled || clientBankaccountRead) && (
            <OfferPartnerBankAccountContainer
              id={'partnerFile_BankAccountCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={partner.partnerId}
              clientBankaccountWrite={clientBankaccountWrite}
            />
          )
        );

      case 'OfferPartnerPropertyContainer':
        return (
          (reorderEnabled || clientPropertiesRead) && (
            <OfferPartnerPropertyContainer
              id={'partnerFile_PropertyCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={partner.partnerId}
            />
          )
        );

      case 'OfferPartnerConsentContainer':
        return (
          reorderEnabled && (
            <OfferPartnerConsentContainer
              id={'partnerFile_ConsentCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={partner.partnerId}
              isAssociatedPartner={true}
              onPrintClick={() => {}}
            />
          )
        );

      case 'OfferPartnerListQuestionnaireContainer':
        return (
          (reorderEnabled || partnerQuestionnaireRead) && (
            <OfferPartnerListQuestionnaireContainer
              id={'partnerFile_QuestionnaireCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={partner.partnerId}
              history={history}
              onChangeActiveStep={() => {}}
            />
          )
        );

      case 'PartnerRelationsContainer':
        return (
          (reorderEnabled || (clientRelationsRead && enablePartnerRelations)) && (
            <PartnerRelationsContainer
              id={'partnerFile_RelationsCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={partner.partnerId}
              startsExpanded={false}
            />
          )
        );

      case 'OfferPartnerShareholderContainer':
        return (
          (reorderEnabled || clientShareholdersRead) && (
            <OfferPartnerShareholderContainer
              id={'partnerFile_ShareholderCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={partner.partnerId}
              openMasterPartner={openMasterPartner}
              openDocumentPartner={openDocumentPartner}
              isAssociatedPartner={true}
              clientShareholdersWrite={clientShareholdersWrite}
              masterPartnerApp={masterPartnerApp && masterPartnerRead}
            />
          )
        );

      case 'OfferPartnerActivityFieldsContainer':
        return (
          (reorderEnabled || clientActivityfieldsRead) && (
            <OfferPartnerActivityFieldsContainer
              id={'partnerFile_ActivityFieldsCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={partner.partnerId}
            />
          )
        );

      case 'OfferPartnerVerificationContainer':
        return (
          (reorderEnabled || clientVerificationsRead) && (
            <OfferPartnerVerificationContainer
              id={'partnerFile_VerificationCard'}
              documentId={documentId}
              siteId={siteId}
              partnerId={partner.partnerId}
              isAssociatedPartner={true}
            />
          )
        );

      default:
        return null;
    }
  };

  const getClientName = () =>
    headerInfo.partnerName ? t('OfferEdit.Client.Title') + ': ' + headerInfo.partnerName : t('OfferEdit.Client.Title');
  const getPartnerName = partner => partner.partnerRole + ': ' + partner.name;

  window.addEventListener('popstate', function () {
    if (changeIndex === 0) return;

    setChangeIndex(0);
    history.push(`/partner-files/${documentId}/${siteId}`);
  });

  return (
    <Box px={2}>
      <AppBar position='static' color='default'>
        <Grid container justifyContent='space-between'>
          <Grid item>
            <Tabs value={changeIndex} onChange={openClient} indicatorColor='primary' textColor='primary' variant='scrollable'>
              <Tab id={'partnerFile_client'} label={getClientName()} />
              {allowedRolesAssociatedPartners?.map((partner, index) => {
                return <Tab id={'partnerFile_associatedPartner_' + index} key={partner.partnerId} label={getPartnerName(partner)} />;
              })}
            </Tabs>
          </Grid>
          <Grid item>
            <Tabs value={0} indicatorColor='primary' textColor='primary' variant='fullWidth'>
              {associatedPartnersLoaded && (
                <Tab
                  id={'partnerFile_associatedPartners'}
                  onClick={handleSelectAssociatedPartner}
                  label={selectedPartnerName === '' ? t('OfferEdit.Client.AssociatedPartner') : selectedPartnerName}
                  wrapped
                />
              )}
              {anchorEl !== null && associatedPartnersLoaded && associatedPartners.length > 0 && (
                <Menu id='decision-type-menu' anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                  {associatedPartners.map(p => {
                    return (
                      <MenuItem key={'partnerFile_associatedPartners_' + p.partnerId} onClick={() => openAssociatedPartner(p.partnerId)}>
                        {getPartnerName(p)}
                      </MenuItem>
                    );
                  })}
                </Menu>
              )}
            </Tabs>
          </Grid>
        </Grid>
      </AppBar>
      <>
        {changeIndex === 0 && !partnerId && (
          <ReorderComponentsContainer
            reorderComponentsName={getReorderJsonName(partner)}
            defaultComponentsOrder={getDefaultComponentsOrder(partner)}
            getComponent={getComponent}
            getComponentExtraParams={partner}
          />
        )}
        {allowedRolesAssociatedPartners?.map((p, index) => {
          return (
            index + 1 === changeIndex && (
              <ReorderComponentsContainer
                reorderComponentsName={getReorderJsonName(p)}
                defaultComponentsOrder={getDefaultComponentsOrder(p)}
                getComponent={getAssociatedPartnerComponent}
                getComponentExtraParams={p}
              />
            )
          );
        })}
        {changeIndex === allowedRolesAssociatedPartners.length + 2 &&
          associatedPartnersLoaded &&
          associatedPartners.map((p, _) => {
            return (
              p.partnerId === partnerId && (
                <ReorderComponentsContainer
                  reorderComponentsName={getReorderJsonName(p)}
                  defaultComponentsOrder={getDefaultComponentsOrder(p)}
                  getComponent={getAssociatedPartnerComponent}
                  getComponentExtraParams={p}
                />
              )
            );
          })}
        {changeIndex === allowedRolesAssociatedPartners.length + 1 && (
          <div>
            <OfferPartnerFormContainer
              documentId={documentId}
              siteId={siteId}
              partnerId={defaultPartnerId}
              isAssociatedPartner={true}
              isRealBeneficiary={false}
              onDelete={handleRemovePartner}
              documentTypeId={headerInfo.documentTypeId}
            />
          </div>
        )}
      </>
    </Box>
  );
};

PartnerFileContainer.propTypes = {
  documentId: PropTypes.number,
  siteId: PropTypes.number,
  partnerId: PropTypes.number,
  history: PropTypes.object.isRequired,
  changeIndex: PropTypes.number.isRequired,
  setChangeIndex: PropTypes.func.isRequired
};

export default withRouter(PartnerFileContainer);
